import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Session } from '../../api/session';
import { getProfile, updateProfile } from '../../api/tenant';
import { describePassError } from '../../domain/passErrors';
import { formatPhone, initials, nameError } from '../../domain/people';
import { telegramPhotoUrl } from '../../domain/telegramPhoto';
import type { TelegramApi } from '../../telegram/webapp';
import { Avatar } from '../../ui/Avatar';
import { Skeleton } from '../../ui/Skeleton';
import f from '../pass-form/passForm.module.css';
import s from './more.module.css';

type Field = 'firstName' | 'middleName' | 'lastName';
const FIELDS: Array<{ id: Field; label: string; required: boolean }> = [
  { id: 'firstName', label: 'Имя', required: true },
  { id: 'middleName', label: 'Отчество', required: false },
  { id: 'lastName', label: 'Фамилия', required: false },
];

interface Props {
  session: Session;
  telegram: TelegramApi;
  /** Browser only (#4218): revokes the token kept for a year. Inside Telegram the session ends with the app. */
  onSignOut?: () => void;
}

/** Profile: names and email; the phone and the photo come from Telegram and are read-only (spec §6.9). */
export function ProfileScreen({ session, telegram, onSignOut }: Props) {
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => getProfile(session) });
  const [form, setForm] = useState({ firstName: '', middleName: '', lastName: '', email: '' });
  const [errors, setErrors] = useState<Partial<Record<Field | 'general', string>>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile.data) {
      const p = profile.data;
      setForm({ firstName: p.firstName ?? '', middleName: p.middleName ?? '', lastName: p.lastName ?? '', email: p.email ?? '' });
    }
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () =>
      updateProfile(session, {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim() || null,
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
      }),
    onSuccess: (p) => {
      queryClient.setQueryData(['profile'], p);
      setSaved(true);
    },
    onError: (e) => setErrors({ general: describePassError(e).general ?? 'Не удалось сохранить' }),
  });

  if (!profile.data) return <main className={f.sheet}><Skeleton height={200} /></main>;

  return (
    <main className={f.sheet} aria-labelledby="profile-title">
      <h1 id="profile-title" className={f.title}>
        Профиль
      </h1>
      <div className={s.profilePhoto}>
        <Avatar className={s.avatarLarge} photoUrl={telegramPhotoUrl(telegram.initData)} initials={initials(profile.data.firstName, profile.data.lastName)} />
        {/* Max photos are not shown yet (domain/telegramPhoto.ts accepts Telegram hosts only). */}
        {telegram.messenger !== 'max' && <span className={f.hint}>Фото из профиля Telegram</span>}
      </div>
      {FIELDS.map((fl) => (
        <div key={fl.id} className={f.field}>
          <label htmlFor={`p-${fl.id}`} className={f.label}>
            {fl.label}
          </label>
          <input
            id={`p-${fl.id}`}
            className={errors[fl.id] ? `${f.input} ${f.invalid}` : f.input}
            value={form[fl.id]}
            onChange={(e) => {
              setSaved(false);
              setForm({ ...form, [fl.id]: e.target.value });
            }}
            autoComplete="off"
          />
          {errors[fl.id] && <span className={f.fieldError}>{errors[fl.id]}</span>}
        </div>
      ))}
      <div className={f.field}>
        <label htmlFor="p-email" className={f.label}>
          Email
        </label>
        <input id="p-email" type="email" className={f.input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className={f.field}>
        <span className={f.label}>Телефон</span>
        <span>{formatPhone(profile.data.phone)}</span>
        <span className={f.hint}>Номер из {telegram.name}, изменить его здесь нельзя</span>
      </div>
      <div className={f.footer}>
        {errors.general && <p className={f.error} role="alert">{errors.general}</p>}
        {saved && <p className={f.error}>Сохранено</p>}
        <button
          type="button"
          className={f.submit}
          disabled={save.isPending}
          onClick={() => {
            const next: typeof errors = {};
            for (const fl of FIELDS) {
              const err = nameError(form[fl.id], fl.required);
              if (err) next[fl.id] = err;
            }
            setErrors(next);
            if (Object.keys(next).length === 0) save.mutate();
          }}
        >
          Сохранить
        </button>
        {onSignOut && (
          <button type="button" className={s.signOut} onClick={onSignOut}>
            Выйти
          </button>
        )}
      </div>
    </main>
  );
}
